import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { PROTOCOL_STATUS_LABEL, PROTOCOL_TYPE_LABEL } from '@apleona/shared';
import { api } from '../api';
import { Card, Empty, Loading, Plate, StatusBadge, TypeBadge, formatDateTime, formatKm } from '../components/ui';

export const ProtokollePage: React.FC = () => {
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  const params = new URLSearchParams({ page: String(page), pageSize: '25' });
  if (q) params.set('q', q);
  if (type) params.set('type', type);
  if (status) params.set('status', status);
  if (from) params.set('from', from);
  if (to) params.set('to', to);

  const { data, isLoading } = useQuery({
    queryKey: ['protokolle', params.toString()],
    queryFn: () => api<{ total: number; items: any[]; page: number; pageSize: number }>(`/protokolle?${params}`),
  });

  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  const reset = (fn: () => void) => { fn(); setPage(1); };

  return (
    <div className="grid" style={{ gap: 16 }}>
      <Card title="Filter">
        <div className="toolbar">
          <div className="field grow">
            <label>Suche</label>
            <input
              value={q}
              onChange={(e) => reset(() => setQ(e.target.value))}
              placeholder="Beleg-Nr., Kennzeichen, Nutzer …"
            />
          </div>
          <div className="field">
            <label>Vorgangsart</label>
            <select value={type} onChange={(e) => reset(() => setType(e.target.value))}>
              <option value="">alle</option>
              {Object.entries(PROTOCOL_TYPE_LABEL).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Status</label>
            <select value={status} onChange={(e) => reset(() => setStatus(e.target.value))}>
              <option value="">alle</option>
              {Object.entries(PROTOCOL_STATUS_LABEL).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>von</label>
            <input type="date" value={from} onChange={(e) => reset(() => setFrom(e.target.value))} />
          </div>
          <div className="field">
            <label>bis</label>
            <input type="date" value={to} onChange={(e) => reset(() => setTo(e.target.value))} />
          </div>
        </div>
      </Card>

      <Card
        title={data ? `${data.total} Vorgänge` : 'Vorgänge'}
        bodyless
        actions={
          pages > 1 ? (
            <>
              <button className="btn sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Zurück</button>
              <span className="small muted">Seite {page} / {pages}</span>
              <button className="btn sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Weiter</button>
            </>
          ) : null
        }
      >
        {isLoading ? (
          <Loading />
        ) : !data || data.items.length === 0 ? (
          <Empty text="Keine Vorgänge gefunden" hint="Filter anpassen oder in der App einen Vorgang erfassen." />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Beleg-Nr.</th>
                  <th>Vorgang</th>
                  <th>Fahrzeug</th>
                  <th>Nutzer / Fahrer</th>
                  <th>Zeitpunkt</th>
                  <th className="num">km</th>
                  <th className="num">Sch.</th>
                  <th className="num">Fotos</th>
                  <th>Erfasst von</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((p) => {
                  const person = p.newUser ?? p.previousUser;
                  return (
                    <tr key={p.id}>
                      <td><Link className="mono" to={`/protokolle/${p.id}`}>{p.number}</Link></td>
                      <td><TypeBadge type={p.type} /></td>
                      <td>
                        <Plate value={p.vehicle.plate} />
                        <div className="small muted">{[p.vehicle.make, p.vehicle.model].filter(Boolean).join(' ')}</div>
                      </td>
                      <td>{person ? `${person.firstName ?? ''} ${person.lastName}`.trim() : '—'}</td>
                      <td>{formatDateTime(p.occurredAt)}</td>
                      <td className="num">{formatKm(p.mileage)}</td>
                      <td className="num">{p._count.damages || '—'}</td>
                      <td className="num">{p._count.photos}</td>
                      <td className="small">{p.coordinator.firstName} {p.coordinator.lastName}</td>
                      <td><StatusBadge status={p.status} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};
